import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Plus, Search, Sparkles, Wand2, RefreshCw, Loader2, ChevronDown, X,
  UploadCloud, Download, Trash2, Mail, Building2, Tag, Filter, Check, Send,
  HelpCircle, Phone, ClipboardList, ClipboardPaste, UserPlus, Plug, FileSpreadsheet, ArrowRight,
} from "lucide-react";
import { PasteLeadsModal } from "@/components/PasteLeadsModal";
import { AddFirstLeadModal } from "@/components/AddFirstLeadModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, API_BASE, errorMessage, tokenStore, orgStore } from "@/lib/api";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { LEADS_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";

const STAGE_COLORS = {
  indigo: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300",
  violet: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  fuchsia: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-300",
};

function ScoreBadge({ score }) {
  if (score == null) return <span className="text-xs text-white/30">—</span>;
  const c = score >= 80 ? "emerald" : score >= 60 ? "amber" : score >= 30 ? "violet" : "rose";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-mono ${STAGE_COLORS[c]}`}>
      {score}
    </span>
  );
}

function StagePill({ stage, color = "violet" }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${STAGE_COLORS[color] || STAGE_COLORS.violet}`}>
      {stage}
    </span>
  );
}

export default function Leads() {
  const leadsTip = useSamTip("leads");
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stages, setStages] = useState([]);
  const [lists, setLists] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("__all");
  const [listFilter, setListFilter] = useState("__all");
  const [scoreMin, setScoreMin] = useState("");
  const [scoreMax, setScoreMax] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [tab, setTab] = useState("table");

  // selection
  const [selected, setSelected] = useState(new Set());
  const [actingBulk, setActingBulk] = useState(false);

  // detail drawer
  const [detail, setDetail] = useState(null);

  // create modal
  const [createOpen, setCreateOpen] = useState(false);
  // Iter 22 (audit D3) — paste-first lead onboarding state.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [addOneOpen, setAddOneOpen] = useState(false);

  // import wizard
  const [importOpen, setImportOpen] = useState(false);

  // enroll dialog
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollSeq, setEnrollSeq] = useState("");

  const stageByName = useMemo(() => {
    const map = {};
    stages.forEach((s) => { map[s.name] = s; });
    return map;
  }, [stages]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
      });
      if (q) params.append("q", q);
      if (stageFilter !== "__all") params.append("stage", stageFilter);
      if (listFilter !== "__all") params.append("list_id", listFilter);
      if (scoreMin) params.append("min_score", scoreMin);
      if (scoreMax) params.append("max_score", scoreMax);

      const [lp, sp, ll, sq] = await Promise.all([
        api.get(`/leads?${params}`),
        api.get("/pipeline-stages"),
        api.get("/lead-lists"),
        api.get("/sequences"),
      ]);
      setLeads(lp.data.items);
      setTotal(lp.data.total);
      setStages(sp.data);
      setLists(ll.data);
      setSequences(sq.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, [q, stageFilter, listFilter, scoreMin, scoreMax, page]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === leads.length && leads.length > 0) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };
  const clearSel = () => setSelected(new Set());

  // ---- AI score ----
  const scoreLead = async (id) => {
    try {
      toast("Scoring lead…");
      await api.post(`/leads/${id}/score`);
      toast.success("Lead scored");
      fetchAll();
      if (detail?.id === id) {
        const r = await api.get(`/leads/${id}`); setDetail(r.data);
      }
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const scoreBulk = async () => {
    if (selected.size === 0) return;
    setActingBulk(true);
    try {
      toast(`Scoring ${selected.size} leads…`);
      await api.post("/leads/score-bulk", { lead_ids: Array.from(selected) });
      toast.success(`${selected.size} leads scored`);
      clearSel();
      fetchAll();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setActingBulk(false); }
  };

  // ---- AI personalize ----
  const personalize = async (id) => {
    try {
      toast("Generating opener…");
      const r = await api.post(`/leads/${id}/personalize`);
      toast.success("Personalisation saved");
      if (detail?.id === id) setDetail(r.data);
      fetchAll();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  // ---- Stage change ----
  const changeStage = async (id, newStage) => {
    try {
      const r = await api.patch(`/leads/${id}`, { stage: newStage });
      if (detail?.id === id) setDetail(r.data);
      fetchAll();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  // ---- Bulk stage ----
  const bulkSetStage = async (stageName) => {
    if (selected.size === 0) return;
    try {
      await api.post("/leads/bulk", { lead_ids: Array.from(selected), set_stage: stageName });
      toast.success(`Moved to ${stageName}`);
      clearSel();
      fetchAll();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/leads/bulk", { lead_ids: Array.from(selected), delete: true });
      toast.success(`Deleted ${selected.size} leads`);
      clearSel();
      fetchAll();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  // ---- Enroll ----
  const doEnroll = async () => {
    if (!enrollSeq || selected.size === 0) return;
    try {
      const r = await api.post(`/sequences/${enrollSeq}/enroll`, { lead_ids: Array.from(selected) });
      toast.success(`Enrolled ${r.data.enrolled}, skipped ${r.data.skipped}`);
      setEnrollOpen(false);
      clearSel();
      fetchAll();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  // ---- Export ----
  const exportCsv = () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (stageFilter !== "__all") params.append("stage", stageFilter);
    if (listFilter !== "__all") params.append("list_id", listFilter);
    const url = `${API_BASE}/leads/export?${params.toString()}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${tokenStore.get()}`, "X-Org-Id": orgStore.get() || "" },
    }).then((r) => r.blob()).then((b) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = "leads.csv";
      a.click();
    }).catch((e) => toast.error("Export failed: " + e.message));
  };

  return (
    <div className="mx-auto max-w-7xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-eyebrow text-sky-400" data-testid="leads-eyebrow">Smart leads</div>
            <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
              Your <span className="brand-text">pipeline</span>, scored.
            </h1>
            <p className="mt-2 max-w-xl text-white/55">
              Import, score, segment, and personalize every lead. AI grades fit and writes a tailored opener so reps walk into every call warm.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="leads-import-btn">
              <UploadCloud className="mr-1.5 h-4 w-4" /> Import CSV
            </Button>
            <Button onClick={() => setPasteOpen(true)} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="leads-paste-btn">
              <ClipboardPaste className="mr-1.5 h-4 w-4" /> Paste
            </Button>
            <Button onClick={exportCsv} variant="outline" className="rounded-xl border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="leads-export-btn">
              <Download className="mr-1.5 h-4 w-4" /> Export
            </Button>
            <Button onClick={() => setCreateOpen(true)} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="leads-new-btn">
              <Plus className="mr-1.5 h-4 w-4" /> Add lead
            </Button>
          </div>
        </div>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="leads-tabs">
          <TabsTrigger value="table" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="leads-tab-table">
            <Users className="mr-1 h-3.5 w-3.5" /> Leads
          </TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="leads-tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="mt-4">
          <SamTipCard
            show={!loading && total === 0}
            enabled={leadsTip.enabled}
            message={leadsTip.message}
            prefill={leadsTip.prefill}
            testId="sam-tip-leads"
            module="leads"
          />
      {/* Filters */}
      <div className="mt-8 glass rounded-2xl p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <Input
              value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Search name, email, company"
              className="border-white/10 bg-black/40 pl-9 text-white placeholder:text-white/40"
              data-testid="leads-search"
            />
          </div>
          <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setPage(1); }}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="leads-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              <SelectItem value="__all">All stages</SelectItem>
              {stages.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={listFilter} onValueChange={(v) => { setListFilter(v); setPage(1); }}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="leads-list-filter"><SelectValue placeholder="List" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              <SelectItem value="__all">All lists</SelectItem>
              {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.member_count})</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input type="number" value={scoreMin} onChange={(e) => { setScoreMin(e.target.value); setPage(1); }} placeholder="Min" className="border-white/10 bg-black/40 text-white" data-testid="leads-score-min" />
            <Input type="number" value={scoreMax} onChange={(e) => { setScoreMax(e.target.value); setPage(1); }} placeholder="Max" className="border-white/10 bg-black/40 text-white" data-testid="leads-score-max" />
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="mt-3 glass flex flex-wrap items-center gap-2 rounded-xl p-3" data-testid="bulk-action-bar">
          <span className="text-sm text-white/65">{selected.size} selected</span>
          <Button size="sm" onClick={scoreBulk} disabled={actingBulk} className="rounded-lg bg-white text-black hover:bg-zinc-200" data-testid="bulk-score">
            {actingBulk ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />} AI Score
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-lg border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="bulk-stage-trigger">
                Move to stage <ChevronDown className="ml-1 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-popover">
              {stages.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => bulkSetStage(s.name)} data-testid={`bulk-stage-${s.name.toLowerCase()}`}>{s.name}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" onClick={() => setEnrollOpen(true)} variant="outline" className="rounded-lg border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="bulk-enroll">
            <Send className="mr-1 h-3.5 w-3.5" /> Enroll in sequence
          </Button>
          <Button size="sm" onClick={bulkDelete} variant="ghost" className="text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="bulk-delete">
            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSel} className="ml-auto text-white/60 hover:text-white">Clear</Button>
        </div>
      )}

      {/* Iter 22 (audit D3) — Guided 4-card empty state when this org has
          NO leads at all (and no filters active). Replaces the previous
          "Import a CSV" empty row that the audit flagged as developer-first. */}
      {!loading && leads.length === 0 && !q && stageFilter === "__all" && listFilter === "__all" ? (
        <div className="mt-6" data-testid="leads-empty-state-hero">
          <div className="mb-5 max-w-2xl">
            <h2 className="font-display text-2xl text-white">Add your first leads</h2>
            <p className="mt-1 text-sm text-white/55">
              Pick the path that matches your week. You can always switch later.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
            {/* Card 1 — Paste (hero, spans 2 cols on lg) */}
            <button
              onClick={() => setPasteOpen(true)}
              className="group relative col-span-1 flex flex-col gap-3 overflow-hidden rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-fuchsia-500/15 via-violet-500/10 to-indigo-500/15 p-5 text-left transition hover:border-fuchsia-300/60 hover:shadow-[0_0_40px_-12px_rgba(217,70,239,0.45)] md:col-span-2 lg:col-span-2"
              data-testid="empty-state-paste"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-fuchsia-400/30 text-fuchsia-100">
                <ClipboardList className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-xl text-white">Paste a list</div>
                <p className="mt-1 text-sm text-white/65">
                  Got names + emails in a doc, sheet, or message thread? Just paste them.
                  We'll figure out the format.
                </p>
              </div>
              <span className="mt-auto inline-flex items-center gap-1 text-xs text-fuchsia-200">
                Start pasting <ArrowRight className="h-3 w-3" />
              </span>
            </button>

            {/* Card 2 — Add one */}
            <button
              onClick={() => setAddOneOpen(true)}
              className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition hover:border-white/20 hover:bg-white/[0.05]"
              data-testid="empty-state-add-one"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/15 text-violet-300">
                <UserPlus className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">Add one at a time</div>
                <p className="mt-1 text-xs text-white/55">A quick form — name + email + optional company.</p>
              </div>
              <span className="mt-auto text-[11px] text-white/45">Manual entry</span>
            </button>

            {/* Card 3 — Connect a source (waitlist) */}
            <Link
              to="/app/integrations"
              className="group flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition hover:border-white/20 hover:bg-white/[0.05]"
              data-testid="empty-state-connect"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-300">
                <Plug className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-medium text-white">Connect a source</div>
                <p className="mt-1 text-xs text-white/55">HubSpot, Pipedrive, Salesforce — join the waitlist.</p>
              </div>
              <span className="mt-auto text-[11px] text-white/45">Integrations</span>
            </Link>

            {/* Card 4 — Import CSV (tertiary, visually de-emphasized) */}
            <button
              onClick={() => setImportOpen(true)}
              className="col-span-full flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.015] px-4 py-3 text-left text-xs text-white/45 transition hover:border-white/10 hover:bg-white/[0.04] hover:text-white/70"
              data-testid="empty-state-csv"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              <span>Have a CSV file?</span>
              <span className="text-white/30">Import the old-fashioned way →</span>
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02]">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="w-12">
                <Checkbox checked={selected.size === leads.length && leads.length > 0} onCheckedChange={selectAll} data-testid="leads-select-all" />
              </TableHead>
              <TableHead className="text-white/40">Name</TableHead>
              <TableHead className="text-white/40">Company</TableHead>
              <TableHead className="text-white/40">Title</TableHead>
              <TableHead className="text-white/40">Stage</TableHead>
              <TableHead className="text-white/40">Score</TableHead>
              <TableHead className="text-white/40">Tags</TableHead>
              <TableHead className="text-white/40"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8} className="text-center text-white/40"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
            {!loading && leads.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-12 text-center text-white/45">
                No leads found. <button onClick={() => setImportOpen(true)} className="text-fuchsia-300 hover:underline">Import a CSV</button> or <button onClick={() => setCreateOpen(true)} className="text-fuchsia-300 hover:underline">add one manually</button>.
              </TableCell></TableRow>
            )}
            {leads.map((l) => (
              <TableRow key={l.id} className="border-white/5" data-testid={`lead-row-${l.id}`}>
                <TableCell><Checkbox checked={selected.has(l.id)} onCheckedChange={() => toggleSelect(l.id)} data-testid={`lead-checkbox-${l.id}`} /></TableCell>
                <TableCell>
                  <button onClick={() => setDetail(l)} className="text-left" data-testid={`lead-open-${l.id}`}>
                    <div className="text-sm text-white hover:underline">{l.name}</div>
                    <div className="text-xs text-white/45">{l.email}</div>
                  </button>
                </TableCell>
                <TableCell className="text-white/80">{l.company || "—"}</TableCell>
                <TableCell className="text-white/65">{l.title || "—"}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="cursor-pointer" data-testid={`lead-stage-trigger-${l.id}`}>
                        <StagePill stage={l.stage} color={stageByName[l.stage]?.color} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="bg-popover">
                      {stages.map((s) => (
                        <DropdownMenuItem key={s.id} onClick={() => changeStage(l.id, s.name)} data-testid={`stage-opt-${l.id}-${s.name.toLowerCase()}`}>
                          <StagePill stage={s.name} color={s.color} />
                          {l.stage === s.name && <Check className="ml-auto h-3.5 w-3.5 text-fuchsia-400" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
                <TableCell><ScoreBadge score={l.score} /></TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(l.tags || []).slice(0, 2).map((t) => <span key={t} className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-white/55">{t}</span>)}
                  </div>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-white/45 hover:text-white"><ChevronDown className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover">
                      <DropdownMenuItem onClick={() => scoreLead(l.id)} data-testid={`row-score-${l.id}`}><Sparkles className="mr-2 h-3.5 w-3.5" /> AI Score</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => personalize(l.id)} data-testid={`row-personalize-${l.id}`}><Wand2 className="mr-2 h-3.5 w-3.5" /> Personalize</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-xs text-white/45">
        <div>{total} leads · page {page} of {Math.max(1, Math.ceil(total / pageSize))}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-white/65 hover:text-white">Prev</Button>
          <Button size="sm" variant="ghost" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage((p) => p + 1)} className="text-white/65 hover:text-white">Next</Button>
        </div>
      </div>
      </>
      )}
        </TabsContent>

        <TabsContent value="guide" className="mt-4">
          <ModuleGuide
            title={LEADS_GUIDE.title}
            whatItIs={LEADS_GUIDE.whatItIs}
            capabilities={LEADS_GUIDE.capabilities}
            howTo={LEADS_GUIDE.howTo}
            ctaLabel={LEADS_GUIDE.cta}
            onCta={() => setTab("table")}
          />
        </TabsContent>
      </Tabs>

      <LeadDetailDrawer
        lead={detail}
        onClose={() => setDetail(null)}
        onScore={() => detail && scoreLead(detail.id)}
        onPersonalize={() => detail && personalize(detail.id)}
        stages={stages}
        stageByName={stageByName}
        onStageChange={(stage) => detail && changeStage(detail.id, stage)}
        onSave={async (patch) => {
          if (!detail) return;
          try {
            const r = await api.patch(`/leads/${detail.id}`, patch);
            setDetail(r.data); fetchAll();
            toast.success("Lead updated");
          } catch (err) { toast.error(errorMessage(err)); }
        }}
      />

      <CreateLeadDialog
        open={createOpen} onOpenChange={setCreateOpen}
        onCreated={() => fetchAll()}
        stages={stages}
      />

      <ImportWizard
        open={importOpen} onOpenChange={setImportOpen}
        lists={lists}
        onDone={() => fetchAll()}
      />

      {/* Iter 22 (audit D3) — Paste + single-form modals for empty-state CTAs */}
      <PasteLeadsModal
        open={pasteOpen} onOpenChange={setPasteOpen}
        onCreated={() => fetchAll()}
      />
      <AddFirstLeadModal
        open={addOneOpen} onOpenChange={setAddOneOpen}
        onCreated={() => { setAddOneOpen(false); fetchAll(); }}
      />

      <AlertDialog open={enrollOpen} onOpenChange={setEnrollOpen}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Enroll {selected.size} leads in a sequence</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">Pick a sequence. Active sequences begin immediately.</AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={enrollSeq} onValueChange={setEnrollSeq}>
            <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="enroll-sequence-select"><SelectValue placeholder="Pick sequence" /></SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
              {sequences.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.status}</SelectItem>)}
            </SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doEnroll} className="bg-white text-black hover:bg-zinc-200" data-testid="enroll-confirm">Enroll</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onScore, onPersonalize, stages, stageByName, onStageChange, onSave }) {
  const [form, setForm] = useState({});
  const [activities, setActivities] = useState([]);
  const [calls, setCalls] = useState([]);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const refreshActivities = useCallback(() => {
    if (!lead) return;
    api.get(`/leads/${lead.id}/activities`).then((r) => setActivities(r.data)).catch(() => {});
    api.get(`/leads/${lead.id}/calls`).then((r) => setCalls(r.data)).catch(() => {});
  }, [lead]);
  useEffect(() => {
    if (lead) {
      setForm({ name: lead.name, email: lead.email || "", company: lead.company || "", title: lead.title || "", phone: lead.phone || "", notes: lead.notes || "" });
      api.get(`/leads/${lead.id}/activities`).then((r) => setActivities(r.data)).catch(() => {});
      api.get(`/leads/${lead.id}/calls`).then((r) => setCalls(r.data)).catch(() => {});
    }
    // Intentionally only re-run when the selected lead's id changes; the drawer
    // re-mounts on close so we don't need to react to other field mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);
  if (!lead) return null;

  return (
    <Sheet open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto border-white/10 bg-[#0a0a0a] text-white sm:max-w-2xl" data-testid="lead-detail-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">{lead.name}</SheetTitle>
          <SheetDescription className="text-white/55">{lead.email || "—"} · {lead.company || "—"}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 flex flex-wrap gap-2">
          <Button size="sm" onClick={onScore} className="rounded-lg bg-white text-black hover:bg-zinc-200" data-testid="detail-score">
            <Sparkles className="mr-1 h-3.5 w-3.5" /> AI Score
          </Button>
          <Button size="sm" onClick={onPersonalize} variant="outline" className="rounded-lg border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="detail-personalize">
            <Wand2 className="mr-1 h-3.5 w-3.5" /> Personalize
          </Button>
          <Button size="sm" onClick={() => setLogCallOpen(true)} variant="outline" className="rounded-lg border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="detail-log-call">
            <Phone className="mr-1 h-3.5 w-3.5" /> Log Call
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-lg border-white/15 bg-white/5 text-white hover:bg-white/10" data-testid="detail-stage-trigger">
                <StagePill stage={lead.stage} color={stageByName[lead.stage]?.color} />
                <ChevronDown className="ml-1 h-3 w-3 text-white/50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-popover">
              {stages.map((s) => (
                <DropdownMenuItem key={s.id} onClick={() => onStageChange(s.name)}>
                  <StagePill stage={s.name} color={s.color} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="ml-auto"><ScoreBadge score={lead.score} /></div>
        </div>

        {lead.score_reason && (
          <div className="mt-3 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm text-white/70" data-testid="detail-score-reason">
            <span className="text-xs uppercase tracking-wider text-white/40">Score rationale</span>
            <div className="mt-1">{lead.score_reason}</div>
          </div>
        )}

        {lead.personalization && (
          <div className="mt-3 rounded-xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-3 text-sm text-white/80" data-testid="detail-personalization">
            <span className="text-xs uppercase tracking-wider text-fuchsia-300">Personalised opener</span>
            <div className="mt-1 italic">"{lead.personalization}"</div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {[
            ["name", "Name"], ["email", "Email"], ["company", "Company"], ["title", "Title"], ["phone", "Phone"],
          ].map(([k, lbl]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">{lbl}</Label>
              <Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="border-white/10 bg-black/40 text-white" data-testid={`detail-${k}`} />
            </div>
          ))}
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Notes</Label>
            <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className="border-white/10 bg-black/40 text-white" data-testid="detail-notes" />
          </div>
        </div>

        <SheetFooter className="mt-4">
          <Button onClick={() => onSave(form)} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="detail-save">Save changes</Button>
        </SheetFooter>

        {/* Call log */}
        <div className="mt-8">
          <div className="flex items-center justify-between">
            <div className="text-eyebrow text-white/40">Call log</div>
            <span className="text-[10px] uppercase tracking-wider text-white/35" data-testid="detail-calls-count">
              {calls.length} {calls.length === 1 ? "call" : "calls"}
            </span>
          </div>
          <div className="mt-3 space-y-2" data-testid="detail-calls">
            {calls.length === 0 ? (
              <div className="text-sm text-white/45">No calls logged yet. Click <span className="text-white/70">Log Call</span> to record one.</div>
            ) : calls.slice(0, 25).map((c) => (
              <div key={c.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-3 text-xs text-white/70" data-testid={`call-row-${c.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-indigo-200">
                    <Phone className="h-3 w-3" /> {c.outcome}
                  </span>
                  {c.duration_minutes != null && (
                    <span className="text-white/55">{c.duration_minutes} min</span>
                  )}
                  {c.next_action && (
                    <span className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2 py-0.5 text-fuchsia-200">{c.next_action}</span>
                  )}
                  <span className="ml-auto text-white/40">{new Date(c.called_at).toLocaleString()}</span>
                </div>
                {c.notes && <div className="mt-2 whitespace-pre-wrap text-white/75">{c.notes}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="text-eyebrow text-white/40">Activity</div>
          <div className="mt-3 space-y-2" data-testid="detail-activities">
            {activities.length === 0 ? (
              <div className="text-sm text-white/45">No activity yet.</div>
            ) : activities.slice(0, 20).map((a) => (
              <div key={a.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-2 text-xs text-white/65">
                <span className="capitalize text-white">{a.type.replace(/_/g, " ")}</span>
                <span className="ml-2 text-white/40">{new Date(a.created_at).toLocaleString()}</span>
                {a.metadata && Object.keys(a.metadata).length > 0 && (
                  <pre className="mt-1 text-[10px] text-white/45 whitespace-pre-wrap">{JSON.stringify(a.metadata)}</pre>
                )}
              </div>
            ))}
          </div>
        </div>

        <LogCallDialog
          open={logCallOpen}
          onOpenChange={setLogCallOpen}
          leadId={lead.id}
          onLogged={() => refreshActivities()}
        />
      </SheetContent>
    </Sheet>
  );
}

const CALL_OUTCOMES = ["Connected", "Voicemail", "No answer", "Disconnected", "Wrong number", "Do not call"];
const CALL_NEXT_ACTIONS = ["Schedule follow-up", "Wait for reply", "Enroll in sequence", "Mark not interested", "Other"];

function LogCallDialog({ open, onOpenChange, leadId, onLogged }) {
  const [form, setForm] = useState({ outcome: "Connected", duration_minutes: "", next_action: "Schedule follow-up", notes: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ outcome: "Connected", duration_minutes: "", next_action: "Schedule follow-up", notes: "" });
    }
  }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        outcome: form.outcome,
        next_action: form.next_action || null,
        notes: form.notes || null,
      };
      if (form.duration_minutes !== "" && form.duration_minutes != null) {
        payload.duration_minutes = Number(form.duration_minutes);
      }
      await api.post(`/leads/${leadId}/calls`, payload);
      toast.success("Call logged");
      onOpenChange(false);
      onLogged?.();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md" data-testid="log-call-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Log a call</DialogTitle>
          <DialogDescription className="text-white/55">
            Capture the outcome and a quick note. Recordings & SIDs sync automatically once Twilio click-to-call ships.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="log-call-form">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Outcome *</Label>
            <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
              <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="call-outcome"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                {CALL_OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">Duration (min)</Label>
              <Input type="number" min="0" max="600" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="border-white/10 bg-black/40 text-white" data-testid="call-duration" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">Next action</Label>
              <Select value={form.next_action} onValueChange={(v) => setForm({ ...form, next_action: v })}>
                <SelectTrigger className="border-white/10 bg-black/40 text-white" data-testid="call-next-action"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                  {CALL_NEXT_ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-white/50">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} placeholder="What did they say? Next steps?" className="border-white/10 bg-black/40 text-white" data-testid="call-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="call-save">
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} <Phone className="mr-1 h-3.5 w-3.5" /> Log call
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateLeadDialog({ open, onOpenChange, onCreated, stages }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", title: "", phone: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leads", form);
      toast.success("Lead added");
      onOpenChange(false);
      setForm({ name: "", email: "", company: "", title: "", phone: "" });
      onCreated?.();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md" data-testid="create-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add lead</DialogTitle>
          <DialogDescription className="text-white/55">Add a single lead manually.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-lead-form">
          {[["name","Full name *",true],["email","Email"],["company","Company"],["title","Title"],["phone","Phone"]].map(([k, lbl, req]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">{lbl}</Label>
              <Input required={!!req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="border-white/10 bg-black/40 text-white" data-testid={`new-lead-${k}`} />
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-white/65 hover:text-white">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="new-lead-save">
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Add lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportWizard({ open, onOpenChange, lists, onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [listId, setListId] = useState("__none");
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobResult, setJobResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep(1); setFile(null); setPreview(null); setMapping({}); setListId("__none"); setJobId(null); setJobResult(null);
    }
  }, [open]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await fetch(`${API_BASE}/leads/import/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}`, "X-Org-Id": orgStore.get() || "" },
        body: fd,
      });
      if (!r.ok) {
        const t = await r.text();
        try { throw new Error(JSON.parse(t).detail); } catch { throw new Error(t); }
      }
      const data = await r.json();
      setPreview(data);
      setMapping(data.suggested_mapping);
      setStep(2);
    } catch (err) { toast.error(err.message || "Preview failed"); }
  };

  const doImport = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      if (listId !== "__none") fd.append("list_id", listId);
      const r = await fetch(`${API_BASE}/leads/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${tokenStore.get()}`, "X-Org-Id": orgStore.get() || "" },
        body: fd,
      });
      if (!r.ok) throw new Error(await r.text());
      const data = await r.json();
      setJobId(data.job_id);
      setStep(3);
      // Poll
      const poll = setInterval(async () => {
        try {
          const jr = await api.get(`/leads/import/${data.job_id}`);
          if (jr.data.status === "done") {
            clearInterval(poll);
            setJobResult(jr.data);
            onDone?.();
          }
        } catch (e) { clearInterval(poll); }
      }, 1000);
    } catch (err) { toast.error(err.message || "Import failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-2xl" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Import leads from CSV</DialogTitle>
          <DialogDescription className="text-white/55">Step {step} of 3 · Map columns → preview → import.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div onClick={() => fileInputRef.current?.click()} className="cursor-pointer rounded-2xl border-2 border-dashed border-white/10 bg-white/[0.02] p-10 text-center hover:border-white/20" data-testid="import-dropzone">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={onPick} className="hidden" data-testid="import-file-input" />
            <UploadCloud className="mx-auto h-9 w-9 text-fuchsia-400" />
            <div className="font-display mt-3 text-xl font-light">Drop CSV or click to upload</div>
            <div className="mt-1 text-xs text-white/45">UTF-8 CSV, max 10MB. We auto-detect columns.</div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-xs text-white/60">
              Detected {preview.total_rows} rows. Map each column to a lead field.
            </div>
            <div className="space-y-2" data-testid="mapping-rows">
              {preview.headers.map((h) => (
                <div key={h} className="flex items-center gap-3">
                  <span className="w-1/3 truncate text-sm text-white/85">{h}</span>
                  <Select value={mapping[h] || "_skip"} onValueChange={(v) => setMapping({ ...mapping, [h]: v })}>
                    <SelectTrigger className="flex-1 border-white/10 bg-black/40 text-white" data-testid={`map-${h}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                      {preview.lead_fields.map((f) => <SelectItem key={f} value={f}>{f === "_skip" ? "— skip column —" : f}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-white/50">Assign to list (optional)</Label>
              <Select value={listId} onValueChange={setListId}>
                <SelectTrigger className="border-white/10 bg-black/40 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#0a0a0a] text-white">
                  <SelectItem value="__none">— None —</SelectItem>
                  {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 max-h-40 overflow-auto">
              <div className="text-xs uppercase tracking-wider text-white/40 mb-2">Preview (first 5 rows)</div>
              <table className="text-xs text-white/70 w-full">
                <thead><tr>{preview.headers.map((h) => <th key={h} className="text-left p-1">{h}</th>)}</tr></thead>
                <tbody>{preview.preview.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="p-1 border-t border-white/5">{c}</td>)}</tr>)}</tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(1)} className="text-white/65 hover:text-white">Back</Button>
              <Button onClick={doImport} disabled={submitting} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="import-start">
                {submitting && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} Import {preview.total_rows} rows
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="import-result">
            {!jobResult ? (
              <div className="flex items-center gap-2 text-white/65"><Loader2 className="h-4 w-4 animate-spin" /> Processing import…</div>
            ) : (
              <>
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm text-emerald-200">
                  <Check className="mb-1 h-5 w-5" />
                  Imported <span className="text-white">{jobResult.imported}</span> leads
                  {jobResult.skipped > 0 && <span> · skipped <span className="text-white">{jobResult.skipped}</span></span>}
                </div>
                {jobResult.errors && jobResult.errors.length > 0 && (
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-xs text-white/65 max-h-40 overflow-auto">
                    <div className="text-eyebrow text-white/40 mb-2">First {Math.min(50, jobResult.errors.length)} errors</div>
                    {jobResult.errors.slice(0, 50).map((e, i) => <div key={i}>Row {e.row}: {e.reason}</div>)}
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={() => onOpenChange(false)} className="rounded-xl bg-white text-black hover:bg-zinc-200" data-testid="import-done">Done</Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
